import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge, StatCard } from "@/components/ui";
import { money } from "@/lib/utils";
import { Gauge, AlertTriangle, DollarSign, ShieldCheck } from "lucide-react";

export default function SLA() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/sla").then((r) => setData(r.data)); }, []);
  if (!data) return <Spinner />;

  return (
    <div className="vc-fade-up">
      <PageHeader title="SLA & Penalties" subtitle="Contractual service-level attainment with penalty / fee-reduction tracking per the RFP's SLA requirements." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="SLAs Monitored" value={data.slas.length} icon={ShieldCheck} tone="brand" />
        <StatCard label="Breaches (MTD)" value={data.breaches} icon={AlertTriangle} tone={data.breaches ? "red" : "brand"} />
        <StatCard label="Penalty Exposure" value={money(data.total_penalty)} sub="Fee reduction" icon={DollarSign} tone={data.total_penalty ? "red" : "brand"} />
        <StatCard label="Monthly Platform Fee" value={money(data.monthly_fee)} icon={Gauge} tone="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.slas.map((s) => {
          const pct = Math.min(100, (s.attainment / s.target) * 100);
          return (
            <Card key={s.name} className="p-5" data-testid="sla-card">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h3 className="font-display font-semibold">{s.name}</h3>
                  <p className="text-xs text-muted-foreground">{s.description}</p>
                </div>
                <Badge tone={s.breached ? "Below Target" : "Above Target"}>{s.breached ? "Breached" : "Met"}</Badge>
              </div>
              <div className="flex items-end justify-between mt-4">
                <div>
                  <p className="text-3xl font-bold font-display">{s.display}</p>
                  <p className="text-xs text-muted-foreground">Target {s.target}% · {s.volume}</p>
                </div>
                {s.breached && <p className="text-sm font-semibold text-red-600">−{money(s.penalty)}<span className="text-xs text-muted-foreground"> ({s.penalty_rate}% of fee)</span></p>}
              </div>
              <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                <div className={`h-full rounded-full ${s.breached ? "bg-red-500" : "bg-brand-600"}`} style={{ width: `${pct}%` }} />
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-5 mt-4 bg-brand-950 text-white border-0">
        <p className="text-sm text-brand-100">
          Penalty model applies a defined fee reduction per breached SLA (per RFP §3.3). Attainment is computed
          in real time from utilization, claims, and contact-center operational data for the selected tenant.
        </p>
      </Card>
    </div>
  );
}
